import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select } from '../components/UI';
import T from '../styles/theme';
import Icon from './Icon';
import { Badge, Btn, ProgressBar, Checkbox } from './UI';
import { useStore } from '../data/store';
import { useToast } from './Toast';
import apiFetch from '../data/api';
import supabase from '../data/supabase';

/* ═══════════════════════════════════════════
   CAMPAIGNS — Email Blast Management
   ═══════════════════════════════════════════ */

const crd = { background: T.card, border: "1px solid " + T.bdr, borderRadius: 14, padding: 16 };
const lbl = { fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 };
const inp = { background: T.bg, border: "1px solid " + T.bdr, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: T.txt, outline: "none", fontFamily: "inherit" };

const MERGE_FIELDS = [
  { tag: "{{first_name}}", label: "First Name", example: "Hunter" },
  { tag: "{{last_name}}", label: "Last Name", example: "Hopkins" },
  { tag: "{{full_name}}", label: "Full Name", example: "Hunter Hopkins" },
  { tag: "{{company}}", label: "Company", example: "Cascade AI" },
  { tag: "{{email}}", label: "Email", example: "hunter@cascade.ai" },
  { tag: "{{title}}", label: "Job Title", example: "CEO" },
  { tag: "{{city}}", label: "City", example: "Seattle" },
  { tag: "{{state}}", label: "State", example: "WA" },
];

export default function CampaignsPanel() {
  const toast = useToast();
  const [subView, setSubView] = useState("list"); // list, compose, sending, detail, domains
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await supabase.from("campaigns")
        .select("*, campaign_entries(status)")
        .eq("org_id", window.__coreflow_org_id)
        .order("created_at", { ascending: false })
        .limit(50);
      setCampaigns((data || []).map(c => ({
        ...c,
        stats: {
          total: c.campaign_entries?.length || 0,
          sent: c.campaign_entries?.filter(e => e.status === "sent").length || 0,
          failed: c.campaign_entries?.filter(e => e.status === "failed").length || 0,
        },
      })));
    } catch (e) { console.warn("Campaign load error:", e.message); }
    setLoading(false);
  }, []);

  const loadDomains = useCallback(async () => {
    try {
      const resp = await apiFetch("/api/email/domains", {
        method: "POST",
        body: JSON.stringify({ action: "list", orgId: window.__coreflow_org_id }),
      });
      const data = await resp.json();
      if (data?.error) throw new Error(data.error);
      if (data.domains) setDomains(data.domains);
    } catch (e) {
      console.error("[Campaigns] Failed to load sending domains:", e);
      toast?.error?.("Failed to load sending domains: " + (e.message || "unknown error"));
    }
  }, [toast]);

  useEffect(() => { loadCampaigns(); loadDomains(); }, [loadCampaigns, loadDomains]);

  return (
    <div>
      {/* Sub-nav */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { id: "list", label: "Campaigns" },
          { id: "compose", label: "+ New Campaign" },
          { id: "domains", label: "Sending Domains" },
        ].map(t => (
          <button key={t.id} onClick={() => setSubView(t.id)} style={{
            padding: "7px 16px", borderRadius: 8, border: "1px solid " + (subView === t.id ? T.blue + "30" : T.bdr),
            background: subView === t.id ? T.blue + "08" : "transparent",
            color: subView === t.id ? T.blue : T.tx3, fontSize: 11, fontWeight: 400, cursor: "pointer",
          }}>{t.label}</button>
        ))}
      </div>

      {subView === "list" && <CampaignList campaigns={campaigns} loading={loading} onSelect={c => { setSubView("detail"); }} onRefresh={loadCampaigns} />}
      {subView === "compose" && <ComposeBlast domains={domains} onSent={() => { setSubView("list"); loadCampaigns(); }} />}
      {subView === "domains" && <DomainManager domains={domains} onRefresh={loadDomains} />}
    </div>
  );
}

/* ── Campaign List ── */
function CampaignList({ campaigns, loading, onRefresh }) {
  if (loading) return <div style={{ textAlign: "center", padding: 40, color: T.tx3, fontSize: 12 }}>Loading campaigns...</div>;
  
  if (campaigns.length === 0) return (
    <div style={{ ...crd, textAlign: "center", padding: "48px 24px" }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: T.blue + "08", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
        <Icon name="mail" size={24} color={T.blue} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 400, color: T.txt, marginBottom: 4 }}>No campaigns yet</div>
      <div style={{ fontSize: 12, color: T.tx3 }}>Create your first email blast to reach your contacts at scale.</div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {campaigns.map(c => (
        <div key={c.id} style={{ ...crd, display: "flex", alignItems: "center", gap: 14, padding: "14px 18px" }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: c.status === "completed" ? "#2E8B57" + "10" : c.status === "sending" ? T.blue + "10" : T.tx3 + "10",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name={c.status === "completed" ? "check" : c.status === "sending" ? "send" : "mail"} size={16} color={c.status === "completed" ? "#2E8B57" : c.status === "sending" ? T.blue : T.tx3} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{c.name}</div>
            <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>{c.subject}</div>
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <Stat label="Sent" value={c.stats?.sent || c.sent_count || 0} color={"#2E8B57"} />
            <Stat label="Failed" value={c.stats?.failed || c.failed_count || 0} color={"#E53935"} />
            <Stat label="Total" value={c.total_recipients || 0} color={T.tx3} />
            <Badge color={c.status === "completed" ? "#2E8B57" : c.status === "sending" ? T.blue : T.tx3}>
              {c.status}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 15, fontWeight: 400, color }}>{value}</div>
      <div style={{ fontSize: 9, color: T.tx3, fontWeight: 400 }}>{label}</div>
    </div>
  );
}

/* ── Compose Blast ── */
function ComposeBlast({ domains, onSent }) {
  const store = useStore();
  const contacts = store.contacts || [];
  const orgId = window.__coreflow_org_id;
  const userName = store.currentUser || "";

  const [step, setStep] = useState(1); // 1: recipients, 2: compose, 3: preview, 4: sending
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");

  // Recipient selection
  const [selectionMode, setSelectionMode] = useState("all"); // all, filter, manual
  const [filters, setFilters] = useState({ leadStage: "", source: "", status: "active", scoreMin: "" });
  const [manualIds, setManualIds] = useState(new Set());
  const [searchQ, setSearchQ] = useState("");

  // Track which field was last focused for merge field insertion
  const lastFocusRef = useRef("body"); // "subject" or "body"

  // Sending state
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);

  // Determine verified domains for "from" dropdown
  const verifiedDomains = domains.filter(d => d.status === "verified");

  // Get filtered contacts
  const getRecipients = () => {
    if (selectionMode === "manual") return contacts.filter(c => manualIds.has(c.id));
    let list = contacts.filter(c => c.email);
    if (filters.status) list = list.filter(c => c.status === filters.status || !filters.status);
    if (filters.leadStage) list = list.filter(c => c.leadStage === filters.leadStage);
    if (filters.source) list = list.filter(c => c.source === filters.source);
    if (filters.scoreMin) list = list.filter(c => (c.score || 0) >= parseInt(filters.scoreMin));
    return list;
  };

  const recipients = getRecipients();
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const recipientCount = recipients.filter(c => c.email && c.email.includes("@") && uuidRegex.test(c.id)).length;

  // Preview: replace merge fields with first contact's data
  const previewContact = recipients[0] || { fn: "John", ln: "Doe", co: "Acme Corp", email: "john@acme.com" };
  const previewSubject = subject
    .replace(/\{\{first_name\}\}/gi, previewContact.fn || previewContact.first_name || "John")
    .replace(/\{\{last_name\}\}/gi, previewContact.ln || previewContact.last_name || "Doe")
    .replace(/\{\{company\}\}/gi, previewContact.co || previewContact.company || "")
    .replace(/\{\{email\}\}/gi, previewContact.email || "")
    .replace(/\{\{full_name\}\}/gi, [previewContact.fn || previewContact.first_name, previewContact.ln || previewContact.last_name].filter(Boolean).join(" "));
  const previewBody = body
    .replace(/\{\{first_name\}\}/gi, previewContact.fn || previewContact.first_name || "John")
    .replace(/\{\{last_name\}\}/gi, previewContact.ln || previewContact.last_name || "Doe")
    .replace(/\{\{company\}\}/gi, previewContact.co || previewContact.company || "")
    .replace(/\{\{email\}\}/gi, previewContact.email || "")
    .replace(/\{\{full_name\}\}/gi, [previewContact.fn || previewContact.first_name, previewContact.ln || previewContact.last_name].filter(Boolean).join(" "));

  // Send the blast
  const handleSend = async () => {
    if (!subject || !body || recipientCount === 0) return;
    setSending(true);
    setStep(4);

    // Filter to valid UUID contact IDs (exclude seed data like "contact-1")
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const contactIds = recipients.filter(c => c.email?.includes("@") && uuidRegex.test(c.id)).map(c => c.id);
    
    if (contactIds.length === 0) {
      setProgress({ error: "No valid contacts to send to", sent: 0, failed: 0, total: 0 });
      setSending(false);
      return;
    }

    let startAfter = 0;
    let campaignId = null;
    let totalSent = 0;
    let totalFailed = 0;

    // Send in batches (API processes 100 per call, we loop until done)
    while (true) {
      try {
        const resp = await apiFetch("/api/email/blast", {
          method: "POST",
          body: JSON.stringify({
            action: "send",
            campaignId,
            contactIds,
            subject,
            body,
            fromName: fromName || undefined,
            fromEmail: fromEmail || undefined,
            replyTo: replyTo || undefined,
            campaignName: name || `Campaign ${new Date().toLocaleDateString()}`,
            startAfter,
            orgId,
          }),
        });
        
        let data;
        try { data = await resp.json(); } catch { data = { success: false, error: "Server returned invalid response" }; }

        if (!data.success) {
          setProgress({ error: data.error || "Send failed", sent: totalSent, failed: totalFailed, total: contactIds.length });
          break;
        }

        campaignId = data.campaignId;
        totalSent += data.sent;
        totalFailed += data.failed;
        setProgress({ sent: totalSent, failed: totalFailed, total: data.total, processed: data.processed });

        if (data.isComplete) break;
        startAfter = data.continueFrom;
      } catch (e) {
        setProgress({ error: e.message, sent: totalSent, failed: totalFailed, total: contactIds.length });
        break;
      }
    }

    setSending(false);
  };

  // Get unique values for filter dropdowns
  const leadStages = [...new Set(contacts.map(c => c.leadStage).filter(Boolean))];
  const sources = [...new Set(contacts.map(c => c.source).filter(Boolean))];

  return (
    <div>
      {/* Step indicator */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {["Recipients", "Compose", "Preview & Send"].map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{
              width: 24, height: 24, borderRadius: "50%",
              background: step > i + 1 ? "#2E8B57" : step === i + 1 ? T.blue : T.bg,
              border: "1.5px solid " + (step > i + 1 ? "#2E8B57" : step === i + 1 ? T.blue : T.bdr),
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 400, color: step >= i + 1 ? "#fff" : T.tx3,
            }}>{step > i + 1 ? "✓" : i + 1}</div>
            <span style={{ fontSize: 11, fontWeight: 400, color: step === i + 1 ? T.txt : T.tx3 }}>{s}</span>
            {i < 2 && <div style={{ width: 32, height: 1, background: T.bdr, margin: "0 4px" }} />}
          </div>
        ))}
      </div>

      {/* Step 1: Recipients */}
      {step === 1 && (
        <div style={{ ...crd, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Select Recipients</div>
          <div style={{ fontSize: 12, color: T.tx3, marginBottom: 20 }}>Choose who receives this email blast.</div>

          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {[
              { id: "all", label: "All contacts with email" },
              { id: "filter", label: "Filter by criteria" },
              { id: "manual", label: "Select manually" },
            ].map(m => (
              <button key={m.id} onClick={() => setSelectionMode(m.id)} style={{
                padding: "10px 16px", borderRadius: 10, fontSize: 11, fontWeight: 400, cursor: "pointer",
                border: "1.5px solid " + (selectionMode === m.id ? T.blue : T.bdr),
                background: selectionMode === m.id ? T.blue + "08" : "transparent",
                color: selectionMode === m.id ? T.blue : T.tx3,
              }}>{m.label}</button>
            ))}
          </div>

          {selectionMode === "filter" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              <div>
                <div style={lbl}>Lead Stage</div>
                <Select value={filters.leadStage} onChange={e => setFilters(p => ({ ...p, leadStage: e.target.value }))} style={{ ...inp, width: "100%" }}>
                  <option value="">Any</option>
                  {leadStages.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
              <div>
                <div style={lbl}>Source</div>
                <Select value={filters.source} onChange={e => setFilters(p => ({ ...p, source: e.target.value }))} style={{ ...inp, width: "100%" }}>
                  <option value="">Any</option>
                  {sources.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
              <div>
                <div style={lbl}>Status</div>
                <Select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} placeholder="Any" options={[{value:"",label:"Any"},{value:"active",label:"Active"},{value:"inactive",label:"Inactive"}]} />
              </div>
              <div>
                <div style={lbl}>Min Score</div>
                <input type="number" value={filters.scoreMin} onChange={e => setFilters(p => ({ ...p, scoreMin: e.target.value }))} placeholder="0" style={{ ...inp, width: "100%" }} />
              </div>
            </div>
          )}

          {selectionMode === "manual" && (
            <div>
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search contacts..." style={{ ...inp, width: "100%", marginBottom: 10 }} />
              <div style={{ maxHeight: 260, overflow: "auto", border: "1px solid " + T.bdr, borderRadius: 8, padding: 4 }}>
                {contacts.filter(c => c.email && (
                  !searchQ || [c.fn, c.ln, c.email, c.co].join(" ").toLowerCase().includes(searchQ.toLowerCase())
                )).slice(0, 100).map(c => (
                  <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 11 }}>
                    <Checkbox checked={manualIds.has(c.id)} onChange={() => {
                      setManualIds(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; });
                    }} />
                    <span style={{ fontWeight: 400, color: T.txt }}>{c.fn} {c.ln}</span>
                    <span style={{ color: T.tx3 }}>{c.email}</span>
                    {c.co && <span style={{ color: T.tx3, marginLeft: "auto" }}>{c.co}</span>}
                  </label>
                ))}
              </div>
              {selectionMode === "manual" && <div style={{ fontSize: 10, color: T.tx3, marginTop: 6 }}>{manualIds.size} selected</div>}
            </div>
          )}

          <div style={{ padding: "14px 0", borderTop: "1px solid " + T.bdr, marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>
              <span style={{ fontSize: 22, fontWeight: 400, color: T.blue }}>{recipientCount}</span> recipients with valid email
            </div>
            <Btn primary disabled={recipientCount === 0} onClick={() => setStep(2)}>Next: Compose →</Btn>
          </div>
        </div>
      )}

      {/* Step 2: Compose */}
      {step === 2 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 14 }}>
          <div style={{ ...crd, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 400, color: T.txt, marginBottom: 16 }}>Compose Email</div>

            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Campaign Name</div>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Q1 Product Update" style={{ ...inp, width: "100%" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <div style={lbl}>From Name</div>
                <input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="e.g. Hunter Hopkins" style={{ ...inp, width: "100%" }} />
              </div>
              <div>
                <div style={lbl}>From Email</div>
                <input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="e.g. noreply@core-flow.io" style={{ ...inp, width: "100%" }} />
                {verifiedDomains.length > 0 && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                    {verifiedDomains.map(d => (
                      <button key={d.id} onClick={() => setFromEmail(`noreply@${d.domain}`)} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid " + T.bdr, background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>@{d.domain}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Reply-To</div>
              <input value={replyTo} onChange={e => setReplyTo(e.target.value)} placeholder="you@company.com" style={{ ...inp, width: "100%" }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Subject Line</div>
              <input value={subject} onChange={e => setSubject(e.target.value)} onFocus={() => { lastFocusRef.current = "subject"; }} placeholder="Hey {{first_name}}, check this out" style={{ ...inp, width: "100%", fontSize: 14, fontWeight: 400, padding: "10px 12px" }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Email Body</div>
              <textarea value={body} onChange={e => setBody(e.target.value)} onFocus={() => { lastFocusRef.current = "body"; }} rows={12} placeholder={"Hi {{first_name}},\n\nI wanted to reach out about...\n\nBest,\n" + (fromName || "Your Name")} style={{ ...inp, width: "100%", resize: "vertical", lineHeight: 1.7, fontFamily: "inherit" }} />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
              <Btn onClick={() => setStep(1)}>← Back</Btn>
              <Btn primary disabled={!subject || !body} onClick={() => setStep(3)}>Preview & Send →</Btn>
            </div>
          </div>

          {/* Merge field sidebar */}
          <div style={{ ...crd, padding: 14, height: "fit-content" }}>
            <div style={lbl}>Merge Fields</div>
            <div style={{ fontSize: 10, color: T.tx3, marginBottom: 10 }}>Click to insert into subject or body</div>
            {MERGE_FIELDS.map(f => (
              <button key={f.tag} onClick={() => {
                if (lastFocusRef.current === "body") {
                  setBody(prev => prev + f.tag);
                } else {
                  setSubject(prev => prev + f.tag);
                }
              }} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "6px 8px", borderRadius: 6, border: "none",
                background: "transparent", color: T.tx2, fontSize: 10,
                cursor: "pointer", width: "100%", textAlign: "left",
              }}>
                <code style={{ background: T.blue + "10", padding: "2px 6px", borderRadius: 4, color: T.blue, fontWeight: 400, fontSize: 9 }}>{f.tag}</code>
                <span style={{ color: T.tx3, fontSize: 9 }}>{f.example}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Preview & Send */}
      {step === 3 && (
        <div style={{ ...crd, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 400, color: T.txt, marginBottom: 16 }}>Preview & Confirm</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            {/* Preview */}
            <div>
              <div style={lbl}>Email Preview (as seen by {previewContact.fn || previewContact.first_name || "first recipient"})</div>
              <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid " + T.bdr, borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>From: {fromName || "Team"} {fromEmail ? `<${fromEmail}>` : "<noreply@core-flow.io>"}</div>
                <div style={{ fontSize: 10, color: T.tx3, marginBottom: 12 }}>To: {previewContact.email}</div>
                <div style={{ fontSize: 15, fontWeight: 400, color: T.txt, borderBottom: "1px solid " + T.bdr, paddingBottom: 10, marginBottom: 12 }}>{previewSubject}</div>
                <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{previewBody}</div>
              </div>
            </div>

            {/* Summary */}
            <div>
              <div style={lbl}>Campaign Summary</div>
              <div style={{ ...crd, padding: 16, marginBottom: 10 }}>
                {[
                  ["Campaign", name || "Untitled"],
                  ["Recipients", recipientCount + " contacts"],
                  ["From", (fromName || "Team") + (fromEmail ? ` <${fromEmail}>` : " <noreply@core-flow.io>")],
                  ["Reply-to", replyTo || "—"],
                  ["Subject", subject],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 11, borderBottom: "1px solid " + T.bdr + "50" }}>
                    <span style={{ color: T.tx3, fontWeight: 400 }}>{l}</span>
                    <span style={{ color: T.txt, fontWeight: 400, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                  </div>
                ))}
              </div>
              {!fromEmail && (
                <div style={{ background: "#D4763A" + "10", border: "1px solid " + "#D4763A" + "20", borderRadius: 8, padding: "8px 12px", fontSize: 10, color: "#D4763A", marginBottom: 10 }}>
                  Sending from noreply@core-flow.io. Add a verified domain for better deliverability.
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "space-between", borderTop: "1px solid " + T.bdr, paddingTop: 16 }}>
            <Btn onClick={() => setStep(2)}>← Edit</Btn>
            <Btn primary onClick={() => {
              if (window.confirm(`Send this email to ${recipientCount} contacts? This cannot be undone.`)) handleSend();
            }} disabled={sending}>
              {sending ? "Sending..." : `Send to ${recipientCount} contacts`}
            </Btn>
          </div>
        </div>
      )}

      {/* Step 4: Sending Progress */}
      {step === 4 && (
        <div style={{ ...crd, padding: 24, textAlign: "center" }}>
          {progress?.error ? (
            <>
              <div style={{ width: 48, height: 48, borderRadius: 24, background: "rgba(229,57,53,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M12 3l9.5 16.5H2.5L12 3z" stroke="#E53935" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ fontSize: 16, fontWeight: 400, color: "#E53935", marginBottom: 8 }}>Error</div>
              <div style={{ fontSize: 12, color: T.tx3, marginBottom: 16 }}>{progress.error}</div>
            </>
          ) : sending ? (
            <>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📨</div>
              <div style={{ fontSize: 16, fontWeight: 400, color: T.txt, marginBottom: 8 }}>Sending Campaign...</div>
              <div style={{ fontSize: 12, color: T.tx3, marginBottom: 16 }}>
                {progress?.processed || 0} of {progress?.total || recipientCount} processed
              </div>
              <div style={{ maxWidth: 300, margin: "0 auto" }}>
                <ProgressBar value={progress?.processed || 0} max={progress?.total || recipientCount} color={T.blue} />
              </div>
            </>
          ) : (
            <>
              <div style={{ width: 48, height: 48, borderRadius: 24, background: "rgba(46,139,87,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#2E8B57" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ fontSize: 16, fontWeight: 400, color: "#2E8B57", marginBottom: 8 }}>Campaign Sent!</div>
              <div style={{ fontSize: 12, color: T.tx3, marginBottom: 8 }}>
                {progress?.sent || 0} delivered, {progress?.failed || 0} failed
              </div>
              <Btn primary onClick={onSent} style={{ marginTop: 16 }}>Back to Campaigns</Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Domain Manager ── */
function DomainManager({ domains, onRefresh }) {
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [checking, setChecking] = useState(false);

  const addDomain = async () => {
    if (!newDomain.includes(".")) { setError("Enter a valid domain like yourdomain.com"); return; }
    setAdding(true); setError("");
    try {
      const resp = await apiFetch("/api/email/domains", {
        method: "POST",
        body: JSON.stringify({ action: "add", domain: newDomain, orgId: window.__coreflow_org_id }),
      });
      const data = await resp.json();
      if (data.success) {
        setNewDomain("");
        setSelectedDomain(data);
        onRefresh();
      } else {
        setError(data.error || "Failed to add domain");
      }
    } catch (e) { setError(e.message); }
    setAdding(false);
  };

  const verifyDomain = async (domainId) => {
    setChecking(true);
    try {
      const resp = await apiFetch("/api/email/domains", {
        method: "POST",
        body: JSON.stringify({ action: "verify", domainId, orgId: window.__coreflow_org_id }),
      });
      const data = await resp.json();
      console.log("[CoreFlow] Domain verify result:", data);
      // Reload domains to get updated status
      await loadDomains();
    } catch (e) { console.error("[CoreFlow] Verify error:", e); }
    setChecking(false);
  };

  return (
    <div>
      {/* Add domain form */}
      <div style={{ ...crd, padding: 20, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Add Sending Domain</div>
        <div style={{ fontSize: 11, color: T.tx3, marginBottom: 14 }}>Verify your domain to send emails from your address instead of noreply@core-flow.io</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={newDomain} onChange={e => setNewDomain(e.target.value)} onKeyDown={e => e.key === "Enter" && addDomain()} placeholder="yourdomain.com" style={{ ...inp, flex: 1 }} />
          <Btn primary onClick={addDomain} disabled={adding}>{adding ? "Adding..." : "Add Domain"}</Btn>
        </div>
        {error && <div style={{ fontSize: 11, color: "#E53935", marginTop: 6 }}>{error}</div>}
      </div>

      {/* Domain list */}
      {domains.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32, color: T.tx3, fontSize: 12 }}>No domains added yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {domains.map(d => {
            const isVerified = d.status === "verified";
            const statusColor = isVerified ? "#2E8B57" : "#F59E0B";
            return (
              <div key={d.id} style={{ background: "#2C2E33", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", overflow: "hidden" }}>
                {/* Domain Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: !isVerified && d.dns_records?.length ? "1px solid rgba(217,219,221,0.06)" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: statusColor, flexShrink: 0 }} />
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>{d.domain}</span>
                      {isVerified && (
                        <div style={{ fontSize: 11, color: "#2E8B57", marginTop: 2 }}>Ready to send from @{d.domain}</div>
                      )}
                      {!isVerified && (
                        <div style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", marginTop: 2 }}>Add the DNS records below to verify</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 6, background: isVerified ? "rgba(46,139,87,0.12)" : "rgba(245,158,11,0.12)", color: statusColor, fontWeight: 400 }}>
                      {isVerified ? "Verified" : "Pending"}
                    </span>
                    {!isVerified && (
                      <button onClick={() => verifyDomain(d.resend_domain_id)} disabled={checking}
                        style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 400 }}>
                        {checking ? "Checking..." : "Verify"}
                      </button>
                    )}
                  </div>
                </div>

                {/* DNS Records Table */}
                {!isVerified && d.dns_records?.length > 0 && (
                  <div style={{ padding: "0 20px 16px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px 0", color: "rgba(250,250,250,0.4)", fontWeight: 400, fontSize: 10, letterSpacing: ".04em", borderBottom: "1px solid rgba(217,219,221,0.06)", width: 50 }}>TYPE</th>
                          <th style={{ textAlign: "left", padding: "8px 0", color: "rgba(250,250,250,0.4)", fontWeight: 400, fontSize: 10, letterSpacing: ".04em", borderBottom: "1px solid rgba(217,219,221,0.06)", width: 120 }}>NAME</th>
                          <th style={{ textAlign: "left", padding: "8px 0", color: "rgba(250,250,250,0.4)", fontWeight: 400, fontSize: 10, letterSpacing: ".04em", borderBottom: "1px solid rgba(217,219,221,0.06)" }}>VALUE</th>
                          <th style={{ textAlign: "center", padding: "8px 0", color: "rgba(250,250,250,0.4)", fontWeight: 400, fontSize: 10, letterSpacing: ".04em", borderBottom: "1px solid rgba(217,219,221,0.06)", width: 70 }}>STATUS</th>
                          <th style={{ width: 60, borderBottom: "1px solid rgba(217,219,221,0.06)" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.dns_records.map((r, i) => {
                          const rVerified = r.status === "verified";
                          return (
                            <tr key={i} style={{ borderBottom: i < d.dns_records.length - 1 ? "1px solid rgba(217,219,221,0.04)" : "none" }}>
                              <td style={{ padding: "12px 0", verticalAlign: "top" }}>
                                <span style={{ fontSize: 10, fontFamily: "monospace", padding: "2px 8px", borderRadius: 4, background: "rgba(64,89,173,0.12)", color: "#6B83D4" }}>
                                  {r.record_type || r.type}
                                </span>
                              </td>
                              <td style={{ padding: "12px 0", verticalAlign: "top" }}>
                                <span style={{ fontSize: 11, fontFamily: "monospace", color: "#FAFAFA" }}>{r.name}</span>
                                {(r.record_type || r.type) === "MX" && (
                                  <span style={{ fontSize: 10, color: "rgba(250,250,250,0.4)", marginLeft: 6 }}>Priority: 10</span>
                                )}
                              </td>
                              <td style={{ padding: "12px 8px 12px 0", verticalAlign: "top", maxWidth: 400 }}>
                                <div style={{ fontSize: 10, fontFamily: "monospace", color: "rgba(250,250,250,0.7)", wordBreak: "break-all", lineHeight: 1.5, padding: "6px 10px", borderRadius: 6, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.06)", userSelect: "all" }}>
                                  {r.value}
                                </div>
                              </td>
                              <td style={{ padding: "12px 0", textAlign: "center", verticalAlign: "top" }}>
                                <div style={{ width: 6, height: 6, borderRadius: 3, background: rVerified ? "#2E8B57" : "rgba(250,250,250,0.2)", margin: "4px auto" }} />
                              </td>
                              <td style={{ padding: "12px 0", textAlign: "right", verticalAlign: "top" }}>
                                <button onClick={() => { navigator.clipboard.writeText(r.value); }}
                                  style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.04)", color: "rgba(250,250,250,0.6)", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(64,89,173,0.12)"; e.currentTarget.style.color = "#FAFAFA"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "rgba(250,250,250,0.6)"; }}>
                                  Copy
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ fontSize: 10, color: "rgba(250,250,250,0.3)", marginTop: 12 }}>
                      Add these records at your domain registrar. DNS changes can take up to 48 hours to propagate.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
